import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { localISODate, weekdayShort } from "@/lib/utils-tg";

export default function EditActivityModal({ open, onOpenChange, activity, onSaved }) {
  const [form, setForm] = useState({});
  const [departments, setDepartments] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && activity) {
      setForm({
        title: activity.title || "",
        description: activity.description || "",
        date: activity.date || localISODate(),
        start_time: activity.start_time || "",
        end_time: activity.end_time || "",
        department_id: activity.department_id || "",
        category_id: activity.category_id || "",
        duration: "",
      });
      api.get("/departments/mine").then((r) => setDepartments(r.data)).catch(() => {});
    }
  }, [open, activity]);

  const currentDept = departments.find((d) => d.id === form.department_id);
  const categories = currentDept?.categories || [];

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || form.title,
        date: form.date,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        department_id: form.department_id || null,
        category_id: form.category_id || null,
      };
      if (form.duration) payload.duration_minutes = parseDuration(form.duration);
      await api.patch(`/activities/${activity.id}`, payload);
      toast.success("Entry updated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!activity) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-activity-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Edit time entry</DialogTitle>
          <DialogDescription>Update the details below.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <input
              className="tg-input mt-1"
              value={form.title || ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="edit-title-input"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Date</label>
              <input
                type="date"
                className="tg-input mt-1"
                value={form.date || ""}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="edit-date-input"
              />
              {form.date && (
                <div className="text-[10px] text-emerald-700 font-semibold mt-1" data-testid="edit-date-weekday">
                  {weekdayShort(form.date)}
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Start</label>
              <input
                type="time"
                className="tg-input mt-1"
                value={form.start_time || ""}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="edit-start-time"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">End</label>
              <input
                type="time"
                className="tg-input mt-1"
                value={form.end_time || ""}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="edit-end-time"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Department</label>
              <select
                className="tg-input mt-1"
                value={form.department_id || ""}
                onChange={(e) => setForm({ ...form, department_id: e.target.value, category_id: "" })}
                data-testid="edit-dept-select"
              >
                <option value="">— Select —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Category</label>
              <select
                className="tg-input mt-1"
                value={form.category_id || ""}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                disabled={!form.department_id}
                data-testid="edit-cat-select"
              >
                <option value="">— Select —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Duration (optional)</label>
            <input
              className="tg-input mt-1"
              value={form.duration || ""}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              placeholder="e.g. 1h30, 90m, or 1:30"
              data-testid="edit-duration-input"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Enter hours &amp; minutes (1h30 / 90m / 1:30). If left blank, we use Start &amp; End above.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => onOpenChange(false)} className="tg-btn-secondary" data-testid="edit-cancel">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !form.title}
            className="tg-btn-primary disabled:opacity-50"
            data-testid="edit-submit"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function parseDuration(input) {
  if (!input) return 30;
  const s = String(input).trim().toLowerCase();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const colon = s.match(/^(\d+):(\d{1,2})$/);
  if (colon) return parseInt(colon[1], 10) * 60 + parseInt(colon[2], 10);
  const h = s.match(/(\d+)\s*h/);
  const m = s.match(/(\d+)\s*m/);
  let total = 0;
  if (h) total += parseInt(h[1], 10) * 60;
  if (m) total += parseInt(m[1], 10);
  if (!total) {
    const both = s.match(/^(\d+)h(\d+)$/);
    if (both) total = parseInt(both[1], 10) * 60 + parseInt(both[2], 10);
  }
  return total || 30;
}
